import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";

const SUGGESTIONS = [
  "silver ring",
  "18k gold earrings",
  "gold chain",
  "kundan set",
  "mangalsutra",
  "bridal necklace",
  "polki jhumkas",
];

export default function SearchBar({ variant = "desktop", onSubmitted }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [value, setValue] = useState("");
  const [placeholder, setPlaceholder] = useState(SUGGESTIONS[0]);
  const [placeIdx, setPlaceIdx] = useState(0);
  const [focused, setFocused] = useState(false);

  // Rotate the placeholder every 2.4s so the search bar feels alive.
  useEffect(() => {
    if (focused || value) return; // pause rotation when the user is typing
    const t = setInterval(() => {
      setPlaceIdx((i) => (i + 1) % SUGGESTIONS.length);
    }, 2400);
    return () => clearInterval(t);
  }, [focused, value]);

  useEffect(() => {
    setPlaceholder(SUGGESTIONS[placeIdx]);
  }, [placeIdx]);

  const submit = (e) => {
    e?.preventDefault?.();
    const q = value.trim();
    if (!q) {
      inputRef.current?.focus();
      return;
    }
    navigate(`/products?search=${encodeURIComponent(q)}`);
    onSubmitted?.();
  };

  const size = variant === "mobile" ? "h-11 text-sm" : "h-12 text-sm";
  const wrap = "w-full";

  return (
    <form
      onSubmit={submit}
      role="search"
      className={`${wrap} relative`}
      data-testid={`searchbar-${variant}`}
    >
      <div className={`${size} flex items-center gap-2 rounded-full border transition-all bg-white ${focused ? "border-[#C5A059] shadow-[0_2px_18px_-8px_rgba(139,30,40,0.35)]" : "border-[#EAE3D9] hover:border-[#C5A059]/60"}`}>
        <Search className="h-4 w-4 text-[#8B1E28] shrink-0 ml-4" />
        <div className="relative flex-1 min-w-0">
          <input
            ref={inputRef}
            type="search"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            className="w-full bg-transparent outline-none py-2 pr-2 placeholder-transparent"
            data-testid={`searchbar-${variant}-input`}
            aria-label="Search products"
          />
          {/* Animated placeholder */}
          {!value && (
            <div className="pointer-events-none absolute inset-0 flex items-center overflow-hidden">
              <span className="text-[#8A8480]">Search&nbsp;</span>
              <span
                key={placeIdx}
                className="text-[#8B1E28] font-medium searchbar-rotate"
                data-testid="searchbar-placeholder"
              >
                &ldquo;{placeholder}&rdquo;
              </span>
            </div>
          )}
        </div>
        {value && (
          <button
            type="button"
            onClick={() => setValue("")}
            className="text-[#8A8480] hover:text-[#8B1E28] mr-1 p-1"
            aria-label="Clear search"
            data-testid={`searchbar-${variant}-clear`}
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <button
          type="submit"
          className="h-full px-4 mr-1 my-1 rounded-full bg-[#8B1E28] text-white text-xs font-semibold uppercase tracking-wider hover:bg-[#7A1A22] transition-colors"
          data-testid={`searchbar-${variant}-submit`}
        >
          Search
        </button>
      </div>
    </form>
  );
}
